import React, { useState, useEffect } from 'react'
import { Mail, Zap, Building2, Shield, ArrowRight, Users, Lightbulb, TrendingUp, GitBranch } from 'lucide-react'
import apiService from '../services/api'
import { toast } from 'react-toastify'
import { 
  getComingSoonTranslations,
  ComingSoonTranslations 
} from '../services/comingSoonTranslations'
import { useLocalization } from '../hooks/useLocalization'
import LanguageToggle from '../components/LanguageToggle'
import ComingSoonSEO from '../components/ComingSoonSEO'

/**
 * Coming Soon Page
 * 
 * Professional, enterprise-grade coming soon page for EvoltBay.
 * Communicates that EvoltBay extends existing solar company ecosystems.
 * 
 * Following OCP: Isolated component, no dependencies on existing modules.
 * 
 * Multilingual support: English and Spanish with automatic language detection.
 */
const ComingSoonPage: React.FC = () => {
  // Use global localization service (backward compatible)
  const { language } = useLocalization()
  const [comingSoonTranslations, setComingSoonTranslations] = useState<ComingSoonTranslations>(
    getComingSoonTranslations('en')
  )
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    message: ''
  })
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Sync Coming Soon translations with global language
  useEffect(() => {
    setComingSoonTranslations(getComingSoonTranslations(language))
  }, [language])


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate form
    if (!formData.contactName || !formData.email || !formData.message) {
      toast.error(language === 'es' 
        ? 'Por favor completa todos los campos requeridos'
        : 'Please fill in all required fields')
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      toast.error(language === 'es'
        ? 'Por favor ingresa una dirección de correo válida'
        : 'Please enter a valid email address')
      return
    }

    // Validate message length
    if (formData.message.trim().length < 10) {
      toast.error(language === 'es'
        ? 'El mensaje debe tener al menos 10 caracteres'
        : 'Message must be at least 10 characters long')
      return
    }

    try {
      setIsSubmitting(true)
      setSubmitted(false)
      
      // Send to backend API
      const response = await apiService.post('/api/contact', {
        companyName: formData.companyName,
        contactName: formData.contactName,
        email: formData.email,
        message: formData.message
      }) as any

      if (response.success) {
        setSubmitted(true)
        toast.success(language === 'es'
          ? '¡Gracias! Tu mensaje ha sido enviado. Te responderemos pronto.'
          : 'Thank you! Your message has been sent. We\'ll get back to you soon.')
        // Reset form after 2 seconds
        setTimeout(() => {
          setSubmitted(false)
          setFormData({ companyName: '', contactName: '', email: '', message: '' })
        }, 2000)
      } else {
        throw new Error(response.message || 'Failed to send message')
      }
    } catch (error: any) {
      console.error('Error submitting contact form:', error)
      toast.error(error.response?.data?.message || error.message || 
        (language === 'es' 
          ? 'Error al enviar el mensaje. Por favor intenta de nuevo.'
          : 'Failed to send message. Please try again.'))
      setSubmitted(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  return (
    <div className="min-h-screen bg-white">
      <ComingSoonSEO language={language} />
      
      {/* Language Toggle - Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageToggle />
      </div>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            {comingSoonTranslations.hero.title}
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {comingSoonTranslations.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="flex items-center text-blue-600">
              <Zap className="w-5 h-5 mr-2" />
              <span className="font-medium">{comingSoonTranslations.hero.comingSoon}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
            {comingSoonTranslations.valueProposition.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Auction Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {comingSoonTranslations.valueProposition.auctions.title}
              </h3>
              <p className="text-gray-600">
                {comingSoonTranslations.valueProposition.auctions.description}
              </p>
            </div>

            {/* Buying & Selling Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {comingSoonTranslations.valueProposition.buyingSelling.title}
              </h3>
              <p className="text-gray-600">
                {comingSoonTranslations.valueProposition.buyingSelling.description}
              </p>
            </div>

            {/* Enterprise RFQs Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {comingSoonTranslations.valueProposition.enterpriseRfqs.title}
              </h3>
              <p className="text-gray-600">
                {comingSoonTranslations.valueProposition.enterpriseRfqs.description}
              </p>
            </div>

            {/* Government Contracting Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {comingSoonTranslations.valueProposition.governmentContracting.title}
              </h3>
              <p className="text-gray-600">
                {comingSoonTranslations.valueProposition.governmentContracting.description}
              </p>
            </div>
          </div>

          {/* Target Audience */}
          <div className="mt-12 bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
              {comingSoonTranslations.valueProposition.targetAudience.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  {comingSoonTranslations.valueProposition.targetAudience.manufacturers.title}
                </h4>
                <p className="text-gray-600 text-sm">
                  {comingSoonTranslations.valueProposition.targetAudience.manufacturers.description}
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  {comingSoonTranslations.valueProposition.targetAudience.distributors.title}
                </h4>
                <p className="text-gray-600 text-sm">
                  {comingSoonTranslations.valueProposition.targetAudience.distributors.description}
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  {comingSoonTranslations.valueProposition.targetAudience.epcs.title}
                </h4>
                <p className="text-gray-600 text-sm">
                  {comingSoonTranslations.valueProposition.targetAudience.epcs.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Model Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {comingSoonTranslations.partnership.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {comingSoonTranslations.partnership.subtitle}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-8 sm:p-10 border border-gray-200 mb-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start mb-6">
                <div className="flex-shrink-0">
                  <GitBranch className="w-6 h-6 text-blue-600 mt-1" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {comingSoonTranslations.partnership.collaborativeDevelopment.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {comingSoonTranslations.partnership.collaborativeDevelopment.description}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {comingSoonTranslations.partnership.directCollaboration.title}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {comingSoonTranslations.partnership.directCollaboration.description}
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <Lightbulb className="w-5 h-5 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {comingSoonTranslations.partnership.featureRequests.title}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {comingSoonTranslations.partnership.featureRequests.description}
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {comingSoonTranslations.partnership.adaptiveSystem.title}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {comingSoonTranslations.partnership.adaptiveSystem.description}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-8 border-2 border-blue-200 border-dashed">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                {comingSoonTranslations.partnership.cta.title}
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {comingSoonTranslations.partnership.cta.description}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a 
                  href="mailto:support@evoltbay.com" 
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  {comingSoonTranslations.partnership.cta.button}
                </a>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                {comingSoonTranslations.partnership.cta.subtext}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
            {comingSoonTranslations.contact.title}
          </h2>
          <p className="text-gray-600 text-center mb-8">
            {comingSoonTranslations.contact.subtitle}
          </p>

          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-6 sm:p-8 border border-gray-200">
            <div className="space-y-4">
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                  {comingSoonTranslations.contact.form.companyName}
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={language === 'es' ? 'Nombre de tu empresa' : 'Your company name'}
                />
              </div>

              <div>
                <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-2">
                  {comingSoonTranslations.contact.form.contactName}
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={language === 'es' ? 'Tu nombre' : 'Your name'}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {comingSoonTranslations.contact.form.email}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={language === 'es' ? 'tu.correo@empresa.com' : 'your.email@company.com'}
                  required
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  {comingSoonTranslations.contact.form.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={language === 'es' 
                    ? 'Cuéntanos sobre tu interés en EvoltBay...' 
                    : 'Tell us about your interest in EvoltBay...'}
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={submitted || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {comingSoonTranslations.contact.form.sending}
                    </>
                  ) : submitted ? (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      {comingSoonTranslations.contact.form.messageSent}
                    </>
                  ) : (
                    <>
                      {comingSoonTranslations.contact.form.sendMessage}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>
              </div>

              <div className="text-center pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  {comingSoonTranslations.contact.form.orEmail}{' '}
                  <a href="mailto:support@evoltbay.com" className="text-blue-600 hover:text-blue-700 font-medium">
                    support@evoltbay.com
                  </a>
                </p>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-gray-200">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-600">
            {comingSoonTranslations.footer.tagline}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {comingSoonTranslations.footer.copyright.replace('{year}', new Date().getFullYear().toString())}
          </p>
        </div>
      </footer>
    </div>
  )
}

export default ComingSoonPage
